/**
 * Legacy strength-based recording and query functions.
 */

import type {
  WordMastery,
  ExerciseRecord,
  LessonProgress,
  BookProgress,
  OverallStats,
  MasteryLevel,
} from '../../types/progress';
import { MASTERY_THRESHOLDS } from '../../types/progress';
import { calculateStrengthChange, calculateChallengeStrengthChange, calculateDecay } from '../mastery';
import {
  getBookContentStats,
  getTotalContentStats,
  getWordIdsForBook,
  getLessonIdsForBook,
  getLessonExerciseCount,
} from '../contentStatsCore';
import {
  getProgress,
  saveProgress,
  getLocalDateString,
  daysBetween,
  updatePracticeStreak,
  updateAnswerStreak,
} from './progressCore';

// ---------------------------------------------------------------------------
// Exercise / challenge recording
// ---------------------------------------------------------------------------

/**
 * Record an exercise result and update all related progress
 */
export function recordExerciseResult(
  exerciseId: string,
  lessonId: string,
  itemIds: string[],
  isCorrect: boolean
): void {
  const data = getProgress();
  const now = new Date().toISOString();

  // Update exercise record
  const exerciseRecord = data.exerciseResults[exerciseId] || {
    attempts: 0,
    correctAttempts: 0,
    lastAttempt: now,
    lastCorrect: false,
  };
  exerciseRecord.attempts += 1;
  if (isCorrect) {
    exerciseRecord.correctAttempts += 1;
  }
  exerciseRecord.lastAttempt = now;
  exerciseRecord.lastCorrect = isCorrect;
  data.exerciseResults[exerciseId] = exerciseRecord;

  // Update word mastery for each item
  for (const itemId of itemIds) {
    const wordMastery = data.wordMastery[itemId] || {
      strength: 0,
      lastPracticed: now,
      timesCorrect: 0,
      timesIncorrect: 0,
    };

    wordMastery.strength = calculateStrengthChange(wordMastery.strength, isCorrect);
    wordMastery.lastPracticed = now;
    if (isCorrect) {
      wordMastery.timesCorrect += 1;
    } else {
      wordMastery.timesIncorrect += 1;
    }
    data.wordMastery[itemId] = wordMastery;
  }

  // Update lesson progress
  const lessonProgress = data.lessonProgress[lessonId] || {
    started: false,
    exercisesAttempted: [],
    correctExercises: [],
    bestAccuracy: 0,
    lastPracticed: now,
  };

  lessonProgress.started = true;
  lessonProgress.lastPracticed = now;

  if (!lessonProgress.exercisesAttempted.includes(exerciseId)) {
    lessonProgress.exercisesAttempted.push(exerciseId);
  }

  if (isCorrect && !lessonProgress.correctExercises.includes(exerciseId)) {
    lessonProgress.correctExercises.push(exerciseId);
  }

  // Calculate accuracy against total exercises in lesson (not just attempted)
  const totalExercises = getLessonExerciseCount(lessonId);
  const correctCount = lessonProgress.correctExercises.length;
  const currentAccuracy = totalExercises > 0
    ? Math.round((correctCount / totalExercises) * 100)
    : 0;
  lessonProgress.bestAccuracy = Math.max(lessonProgress.bestAccuracy, currentAccuracy);

  data.lessonProgress[lessonId] = lessonProgress;

  // Update overall stats
  data.stats.totalExercisesAttempted += 1;
  if (isCorrect) {
    data.stats.totalCorrect += 1;
  }

  updateAnswerStreak(data.stats, isCorrect);
  updatePracticeStreak(data.stats);

  saveProgress(data);
}

/**
 * Get mastery level for a word based on strength and decay
 */
export function getWordMasteryLevel(wordId: string): MasteryLevel {
  const data = getProgress();
  const mastery = data.wordMastery[wordId];

  if (!mastery || mastery.strength === 0) {
    return 'new';
  }

  // Apply decay if applicable
  const daysSincePractice = daysBetween(
    mastery.lastPracticed.split('T')[0],
    getLocalDateString()
  );

  if (daysSincePractice > MASTERY_THRESHOLDS.DECAY_DAYS) {
    return 'decaying';
  }

  const effectiveStrength = calculateDecay(mastery.strength, daysSincePractice);

  if (effectiveStrength >= MASTERY_THRESHOLDS.MASTERED) {
    return 'mastered';
  }
  if (effectiveStrength >= MASTERY_THRESHOLDS.FAMILIAR) {
    return 'familiar';
  }
  return 'learning';
}

/**
 * Get effective strength for a word (with decay applied)
 */
export function getWordEffectiveStrength(wordId: string): number {
  const data = getProgress();
  const mastery = data.wordMastery[wordId];

  if (!mastery) {
    return 0;
  }

  const daysSincePractice = daysBetween(
    mastery.lastPracticed.split('T')[0],
    getLocalDateString()
  );

  return calculateDecay(mastery.strength, daysSincePractice);
}

/**
 * Check if a lesson is completed (>= 90% accuracy)
 */
export function isLessonCompleted(lessonId: string): boolean {
  const data = getProgress();
  const progress = data.lessonProgress[lessonId];

  if (!progress) {
    return false;
  }

  return progress.bestAccuracy >= MASTERY_THRESHOLDS.LESSON_COMPLETE;
}

/**
 * Get lesson mastery percentage (0-100)
 */
export function getLessonMasteryPercent(lessonId: string): number {
  const data = getProgress();
  const progress = data.lessonProgress[lessonId];
  return progress?.bestAccuracy || 0;
}

/**
 * Get mastery level for a lesson
 */
export function getLessonMasteryLevel(lessonId: string): MasteryLevel {
  const data = getProgress();
  const progress = data.lessonProgress[lessonId];

  if (!progress || !progress.started) {
    return 'new';
  }

  const accuracy = progress.bestAccuracy;

  // Completed lessons (90%+) stay mastered permanently — no decay
  if (accuracy >= MASTERY_THRESHOLDS.LESSON_COMPLETE) {
    return 'mastered';
  }

  // Decay only applies to sub-90% lessons
  const daysSincePractice = daysBetween(
    progress.lastPracticed.split('T')[0],
    getLocalDateString()
  );

  if (daysSincePractice > MASTERY_THRESHOLDS.DECAY_DAYS) {
    return 'decaying';
  }

  if (accuracy >= MASTERY_THRESHOLDS.FAMILIAR) {
    return 'familiar';
  }
  if (accuracy > 0) {
    return 'learning';
  }
  return 'new';
}

/**
 * Get lesson progress details
 */
export function getLessonProgress(lessonId: string): LessonProgress | null {
  const data = getProgress();
  return data.lessonProgress[lessonId] || null;
}

/**
 * Count words learned (strength >= 80) for a book or all books
 */
export function getWordsLearnedCount(bookNumber?: number): number {
  const wordIds = bookNumber
    ? getWordIdsForBook(bookNumber)
    : [...getWordIdsForBook(1), ...getWordIdsForBook(2), ...getWordIdsForBook(3)];

  let count = 0;
  for (const wordId of wordIds) {
    const strength = getWordEffectiveStrength(wordId);
    if (strength >= MASTERY_THRESHOLDS.LEARNED) {
      count += 1;
    }
  }

  return count;
}

/**
 * Count words in progress (strength > 0 but < LEARNED threshold) for a book or all books
 * These are words the user has practiced but haven't fully mastered yet
 */
export function getWordsInProgressCount(bookNumber?: number): number {
  const wordIds = bookNumber
    ? getWordIdsForBook(bookNumber)
    : [...getWordIdsForBook(1), ...getWordIdsForBook(2), ...getWordIdsForBook(3)];

  let count = 0;
  for (const wordId of wordIds) {
    const strength = getWordEffectiveStrength(wordId);
    if (strength > 0 && strength < MASTERY_THRESHOLDS.LEARNED) {
      count += 1;
    }
  }

  return count;
}

/**
 * Count lessons passed (>= 60% accuracy, i.e. unlocked the next lesson) for a book or all books.
 * This matches the user's mental model: "I've done 4 lessons, I'm on lesson 5."
 */
export function getLessonsCompletedCount(bookNumber?: number): number {
  const lessonIds = bookNumber
    ? getLessonIdsForBook(bookNumber)
    : [...getLessonIdsForBook(1), ...getLessonIdsForBook(2), ...getLessonIdsForBook(3)];

  const data = getProgress();
  let count = 0;
  for (const lessonId of lessonIds) {
    const progress = data.lessonProgress[lessonId];
    if (progress && progress.bestAccuracy >= 60) {
      count += 1;
    }
  }

  return count;
}

/**
 * Get progress summary for a specific book
 */
export function getBookProgress(bookNumber: number): BookProgress {
  const contentStats = getBookContentStats(bookNumber);
  const lessonsCompleted = getLessonsCompletedCount(bookNumber);
  const wordsLearned = getWordsLearnedCount(bookNumber);
  const wordsInProgress = getWordsInProgressCount(bookNumber);

  // Calculate mastery percent as average of lesson accuracies
  const lessonIds = getLessonIdsForBook(bookNumber);
  let totalAccuracy = 0;
  let lessonsWithProgress = 0;

  for (const lessonId of lessonIds) {
    const accuracy = getLessonMasteryPercent(lessonId);
    if (accuracy > 0) {
      totalAccuracy += accuracy;
      lessonsWithProgress += 1;
    }
  }

  const masteryPercent = lessonsWithProgress > 0
    ? Math.round(totalAccuracy / lessonIds.length)
    : 0;

  return {
    bookNumber,
    lessonsCompleted,
    totalLessons: contentStats.lessonCount,
    wordsLearned,
    wordsInProgress,
    totalWords: contentStats.wordCount,
    masteryPercent,
  };
}

/**
 * Get overall statistics
 */
export function getOverallStats(): OverallStats {
  const data = getProgress();
  const totalContent = getTotalContentStats();

  const wordsLearned = getWordsLearnedCount();
  const wordsInProgress = getWordsInProgressCount();
  const lessonsCompleted = getLessonsCompletedCount();

  // Calculate grammar points learned (based on lessons completed)
  // Simplified: assume grammar points learned proportionally to lessons completed
  const grammarPoints = Math.round(
    (lessonsCompleted / totalContent.lessonCount) * totalContent.grammarCount
  );

  const accuracy = data.stats.totalExercisesAttempted > 0
    ? Math.round((data.stats.totalCorrect / data.stats.totalExercisesAttempted) * 100)
    : 0;

  return {
    wordsLearned,
    wordsInProgress,
    totalWords: totalContent.wordCount,
    grammarPoints,
    totalGrammar: totalContent.grammarCount,
    lessonsCompleted,
    totalLessons: totalContent.lessonCount,
    accuracy,
    currentAnswerStreak: data.stats.currentAnswerStreak,
    bestAnswerStreak: data.stats.bestAnswerStreak,
    currentPracticeStreak: data.stats.currentPracticeStreak,
    bestPracticeStreak: data.stats.bestPracticeStreak,
    lastPracticeDate: data.stats.lastPracticeDate,
  };
}

/**
 * Get raw word mastery data
 */
export function getWordMastery(wordId: string): WordMastery | null {
  const data = getProgress();
  return data.wordMastery[wordId] || null;
}

/**
 * Get raw exercise record
 */
export function getExerciseRecord(exerciseId: string): ExerciseRecord | null {
  const data = getProgress();
  return data.exerciseResults[exerciseId] || null;
}

/**
 * Record a CHALLENGE exercise result.
 * Uses higher stakes scoring: +15/-30 instead of +10/-20.
 * On success, marks the word as having proven mastery (slower decay).
 */
export function recordChallengeResult(
  exerciseId: string,
  lessonId: string,
  itemIds: string[],
  isCorrect: boolean
): void {
  const data = getProgress();
  const now = new Date().toISOString();
  const today = getLocalDateString();

  // Update exercise record
  const exerciseRecord = data.exerciseResults[exerciseId] || {
    attempts: 0,
    correctAttempts: 0,
    lastAttempt: now,
    lastCorrect: false,
  };
  exerciseRecord.attempts += 1;
  if (isCorrect) {
    exerciseRecord.correctAttempts += 1;
  }
  exerciseRecord.lastAttempt = now;
  exerciseRecord.lastCorrect = isCorrect;
  data.exerciseResults[exerciseId] = exerciseRecord;

  // Update word mastery for each item with CHALLENGE scoring
  for (const itemId of itemIds) {
    const wordMastery = data.wordMastery[itemId] || {
      strength: 0,
      lastPracticed: now,
      timesCorrect: 0,
      timesIncorrect: 0,
      challengesPassed: 0,
      lastChallengeDate: null,
    };

    // Use challenge scoring (+15/-30)
    wordMastery.strength = calculateChallengeStrengthChange(wordMastery.strength, isCorrect);
    wordMastery.lastPracticed = now;

    if (isCorrect) {
      wordMastery.timesCorrect += 1;
      // Mark as proven mastery for slower decay
      wordMastery.challengesPassed = (wordMastery.challengesPassed || 0) + 1;
      wordMastery.lastChallengeDate = today;
    } else {
      wordMastery.timesIncorrect += 1;
    }

    data.wordMastery[itemId] = wordMastery;
  }

  // Update lesson progress (same as regular exercise)
  const lessonProgress = data.lessonProgress[lessonId] || {
    started: false,
    exercisesAttempted: [],
    correctExercises: [],
    bestAccuracy: 0,
    lastPracticed: now,
  };

  lessonProgress.started = true;
  lessonProgress.lastPracticed = now;

  if (!lessonProgress.exercisesAttempted.includes(exerciseId)) {
    lessonProgress.exercisesAttempted.push(exerciseId);
  }

  if (isCorrect && !lessonProgress.correctExercises.includes(exerciseId)) {
    lessonProgress.correctExercises.push(exerciseId);
  }

  // Calculate accuracy against total exercises in lesson (not just attempted)
  const totalExercises = getLessonExerciseCount(lessonId);
  const correctCount = lessonProgress.correctExercises.length;
  const currentAccuracy = totalExercises > 0
    ? Math.round((correctCount / totalExercises) * 100)
    : 0;
  lessonProgress.bestAccuracy = Math.max(lessonProgress.bestAccuracy, currentAccuracy);

  data.lessonProgress[lessonId] = lessonProgress;

  // Update overall stats
  data.stats.totalExercisesAttempted += 1;
  if (isCorrect) {
    data.stats.totalCorrect += 1;
  }

  updateAnswerStreak(data.stats, isCorrect);
  updatePracticeStreak(data.stats);

  saveProgress(data);
}

/**
 * Check if a word has proven mastery (passed at least one challenge)
 */
export function hasProvenMastery(wordId: string): boolean {
  const data = getProgress();
  const mastery = data.wordMastery[wordId];
  return mastery?.challengesPassed !== undefined && mastery.challengesPassed > 0;
}
